import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PawPrint, Package, ShoppingCart, Users, IndianRupee, BarChart3, LogOut, Box, Scissors, CalendarDays, FileText, MessageSquare, ImageIcon, Home, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminLayout({ children, active }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleLogout = async () => { await logout(); navigate('/'); };

  const links = [
    { href: '/admin', label: 'Dashboard', icon: BarChart3 },
    { href: '/admin/products', label: 'Products', icon: Box },
    { href: '/admin/orders', label: 'Orders', icon: Package },
    { href: '/admin/services', label: 'Services', icon: Scissors },
    { href: '/admin/appointments', label: 'Appointments', icon: CalendarDays },
    { href: '/admin/users', label: 'Users', icon: Users },
    { href: '/admin/messages', label: 'Messages', icon: MessageSquare },
    { href: '/admin/gallery', label: 'Our Work', icon: ImageIcon },
    { href: '/admin/landing-gallery', label: 'Landing Photos', icon: Home },
  ];

  return (
    <div className="flex min-h-screen bg-cream">
      <aside className="w-64 bg-dark-brown text-cream shrink-0 hidden lg:flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-cream/10">
          <Link to="/" className="flex items-center gap-2"><PawPrint className="w-6 h-6 text-rust" /><span className="font-heading text-lg font-bold">Pets Valley</span></Link>
          <p className="font-body text-xs text-cream/50 mt-1">Admin Panel</p>
        </div>
        <nav className="flex-1 py-4">
          {links.map(l => (
            <Link key={l.href} to={l.href} className={`flex items-center gap-3 px-6 py-3 font-body text-sm transition-colors ${active === l.href ? 'bg-cream/10 text-cream border-r-2 border-rust' : 'text-cream/60 hover:text-cream hover:bg-cream/5'}`} data-testid={`admin-nav-${l.label.toLowerCase()}`}>
              <l.icon className="w-4 h-4" /> {l.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-cream/10">
          <div className="font-body text-xs text-cream/50 mb-2">{user?.email}</div>
          <button onClick={handleLogout} className="flex items-center gap-2 font-body text-sm text-cream/60 hover:text-cream"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-dark-brown text-cream p-4 z-50 shadow-md">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-rust" />
            <span className="font-heading text-lg font-bold">Admin Panel</span>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-1 text-cream hover:text-rust transition-colors" data-testid="admin-mobile-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-dark-brown border-t border-cream/10 p-3 shadow-xl flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
            {links.map(l => (
              <Link 
                key={l.href} 
                to={l.href} 
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 font-body text-sm rounded-xl transition-all ${active === l.href ? 'bg-rust text-cream shadow-md' : 'text-cream/60 hover:bg-cream/10 hover:text-cream'}`}
              >
                <l.icon className="w-5 h-5" /> {l.label}
              </Link>
            ))}
            <div className="h-px bg-cream/10 my-2 mx-2"></div>
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 font-body text-sm text-red-400 hover:bg-red-500/10 rounded-xl transition-all text-left">
              <LogOut className="w-5 h-5" />
              <div className="flex flex-col">
                <span>Logout</span>
                <span className="text-xs opacity-70 truncate max-w-[200px]">{user?.email}</span>
              </div>
            </button>
          </div>
        )}
      </div>
      <main className="flex-1 lg:p-8 p-4 pt-20 lg:pt-8 overflow-auto">{children}</main>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/admin/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <AdminLayout active="/admin"><div className="flex items-center justify-center py-20"><div className="spinner"></div></div></AdminLayout>;

  const cards = [
    { label: 'Products', value: stats?.total_products || 0, icon: Box, color: 'bg-rust/10 text-rust' },
    { label: 'Orders', value: stats?.total_orders || 0, icon: Package, color: 'bg-amber/10 text-amber' },
    { label: 'Users', value: stats?.total_users || 0, icon: Users, color: 'bg-blue-50 text-blue-600' },
    { label: 'Revenue', value: `\u20B9${(stats?.total_revenue || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'bg-green-50 text-green-600', isPrice: true },
    { label: 'Services', value: stats?.total_services || 0, icon: Scissors, color: 'bg-purple-50 text-purple-600' },
    { label: 'Appointments', value: stats?.total_appointments || 0, icon: CalendarDays, color: 'bg-pink-50 text-pink-600' },
  ];

  return (
    <AdminLayout active="/admin">
      <div data-testid="admin-dashboard">
        <h1 className="font-heading text-3xl font-bold text-dark-brown mb-8">Dashboard</h1>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {cards.map(c => (
            <div key={c.label} className="bg-white rounded-xl border border-border-custom p-5" data-testid={`stat-${c.label.toLowerCase()}`}>
              <div className={`w-10 h-10 rounded-lg ${c.color} flex items-center justify-center mb-3`}><c.icon className="w-5 h-5" /></div>
              <div className="text-2xl font-bold text-[#212121]" style={{ fontFamily: 'Roboto, sans-serif', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{c.value}</div>
              <div className="font-body text-xs text-light-brown">{c.label}</div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-border-custom p-6">
          <h2 className="font-heading text-xl font-bold text-dark-brown mb-4">Recent Orders</h2>
          {stats?.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full"><thead><tr className="border-b border-border-custom">
                <th className="text-left font-body text-xs text-light-brown uppercase pb-3">Order</th>
                <th className="text-left font-body text-xs text-light-brown uppercase pb-3">Customer</th>
                <th className="text-left font-body text-xs text-light-brown uppercase pb-3">Payment</th>
                <th className="text-left font-body text-xs text-light-brown uppercase pb-3">Status</th>
                <th className="text-right font-body text-xs text-light-brown uppercase pb-3">Total</th>
              </tr></thead><tbody>
                {stats.recent_orders.map(o => (
                  <tr key={o.id} className="border-b border-border-custom last:border-0">
                    <td className="py-3 font-body text-sm text-dark-brown">#{o.id}</td>
                    <td className="py-3 font-body text-sm text-light-brown">{o.user_email}</td>
                    <td className="py-3 font-body text-xs uppercase text-mid-brown">{o.payment_method || 'razorpay'}</td>
                    <td className="py-3"><span className="px-2 py-1 rounded-full text-xs font-body bg-pale text-mid-brown">{o.status}</span></td>
                    <td className="py-3 text-right font-bold text-[#212121]" style={{ fontFamily: 'Roboto, sans-serif', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{'\u20B9'}{o.total?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          ) : <p className="font-body text-sm text-light-brown">No orders yet</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
